import { REGISTER_API_KEY } from '.';
import authenticationServiceApi from 'shared/api/authentication.service.api';
import API from 'shared/api/API';
import jwtDecode from 'jwt-decode';

// Refresh 30 minutes before access-token expiry. Default access-token TTL is
// 3 hours; using a 30-minute buffer keeps us well clear of expiry while leaving
// enough headroom for retries on a transient failure.
const REFRESH_BUFFER_MS = 30 * 60 * 1000;
const RETRY_DELAY_MS = 30 * 1000;
const MIN_REFRESH_DELAY_MS = 1000;

let refreshTimer = null;
let visibilityListener = null;

const clearScheduledRefresh = () => {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
};

const scheduleNextRefresh = (apiKey, dispatch, accessToken) => {
  clearScheduledRefresh();

  let delay;
  try {
    const { exp } = jwtDecode(accessToken);
    delay = Math.max(MIN_REFRESH_DELAY_MS, exp * 1000 - Date.now() - REFRESH_BUFFER_MS);
  } catch {
    delay = RETRY_DELAY_MS;
  }

  refreshTimer = setTimeout(() => {
    dispatch(exchangeAPIKeyAction({ apiKey }));
  }, delay);
};

const ensureVisibilityListener = (apiKey, dispatch) => {
  if (visibilityListener || typeof document === 'undefined') return;

  visibilityListener = () => {
    if (document.visibilityState === 'visible') {
      // Tab regained focus. setTimeout is throttled in background tabs and
      // may have skipped its scheduled refresh — re-exchange immediately so
      // we never depend on a stale access token.
      dispatch(exchangeAPIKeyAction({ apiKey }));
    }
  };

  document.addEventListener('visibilitychange', visibilityListener);
};

export const exchangeAPIKeyAction =
  ({ apiKey }) =>
  async dispatch => {
    const { data, error } = await authenticationServiceApi.exchangeAPIKeyWithToken({ apiKey });

    if (error) {
      // Schedule a retry instead of giving up. Without this the timer chain
      // dies on a transient network blip and the next 3-hour-old access token
      // hits the (broken) Executer 401-fallback path.
      clearScheduledRefresh();
      refreshTimer = setTimeout(() => {
        dispatch(exchangeAPIKeyAction({ apiKey }));
      }, RETRY_DELAY_MS);

      return {
        error: {
          code: error.response?.data?.code,
        },
      };
    }

    API.setToken(data.accessToken, true);

    const { id } = jwtDecode(data.accessToken);
    scheduleNextRefresh(apiKey, dispatch, data.accessToken);
    ensureVisibilityListener(apiKey, dispatch);

    return { userId: id };
  };

export const registerApiKey =
  ({ apiKey }) =>
  async dispatch => {
    const res = await dispatch(exchangeAPIKeyAction({ apiKey }));

    dispatch({
      type: REGISTER_API_KEY,
      payload: { apiKey },
    });

    return res;
  };
